import { effect, signal } from './signals.ts'
import type { ReadonlySignal, Signal } from './signals.ts'
import { localStorageDriver } from './drivers.ts'
import type { StorageDriver } from './drivers.ts'

interface Envelope {
  v: number
  value: unknown
}

function isEnvelope(value: unknown): value is Envelope {
  return typeof value === 'object' && value !== null && typeof (value as Envelope).v === 'number'
}

export interface PersistedSignalOptions<T> {
  driver?: StorageDriver
  /** Bump together with `migrate` when the stored shape changes. */
  version?: number
  migrate?: (value: unknown, fromVersion: number) => T
  /**
   * Checks a stored value before the signal takes it: return it as a `T`, or throw to drop it
   * and keep the current value. Storage outlives the code that wrote it, and a browser
   * extension or a hand in devtools can write it too, so without `parse` a stored value is
   * taken as it is. Runs on load and on every change from another tab.
   *
   * ```ts
   * const theme = persistedSignal<'light' | 'dark'>('theme', 'light', {
   *   parse: (raw) => (raw === 'dark' ? 'dark' : 'light'),
   * })
   * ```
   */
  parse?: (raw: unknown) => T
}

export type PersistedSignal<T> = Signal<T> & { ready: ReadonlySignal<boolean> }

export function persistedSignal<T>(
  key: string,
  initial: T,
  options: PersistedSignalOptions<T> = {},
): PersistedSignal<T> {
  const driver = options.driver ?? localStorageDriver()
  const version = options.version ?? 1
  const value = signal(initial)
  const ready = signal(false)
  // Suppresses the write-through effect while adopting a value FROM storage.
  let adopting = false

  function decode(raw: string | null): T | undefined {
    if (raw === null) return undefined
    try {
      const envelope: unknown = JSON.parse(raw)
      if (!isEnvelope(envelope)) return undefined
      if (envelope.v !== version) {
        if (!options.migrate) return undefined
        const migrated = options.migrate(envelope.value, envelope.v)
        // Persist the migration immediately so old-format data doesn't linger.
        driver.set(key, JSON.stringify({ v: version, value: migrated } satisfies Envelope))
        return migrated
      }
      if (!options.parse) return envelope.value as T
      try {
        return options.parse(envelope.value)
      } catch (error) {
        console.warn(`[cascivo] persistedSignal("${key}") dropped a stored value:`, error)
        return undefined
      }
    } catch {
      return undefined
    }
  }

  function adopt(raw: string | null): void {
    const decoded = decode(raw)
    if (decoded !== undefined) {
      adopting = true
      value.value = decoded
      adopting = false
    }
  }

  const raw = driver.get(key)
  if (raw instanceof Promise) {
    void raw.then((resolved) => {
      adopt(resolved)
      ready.value = true
    })
  } else {
    adopt(raw)
    ready.value = true
  }

  effect(() => {
    const next = value.value
    if (!ready.value || adopting) return
    driver.set(key, JSON.stringify({ v: version, value: next } satisfies Envelope))
  })

  driver.subscribe?.(key, adopt)

  const persisted = value as PersistedSignal<T>
  ;(persisted as { ready: ReadonlySignal<boolean> }).ready = ready
  return persisted
}
